/**
 * The shell both of the editor's pickers stand in — a title, a sentence, a body, and a way out.
 *
 * „Zarządzaj" picks the clients who hold a form; the recording question picks the movement it asks
 * about. What they share is the DIALOG: its backdrop, its box, its head, its close and its foot.
 * What they no longer share is the body, and that is the 17 September 2026 change — the exercise
 * picker is `entities/exercise-library`'s own list now (owner: the picker should be the plan
 * builder's library in a smaller form), which brings its own search field, its own filters and its
 * own four states. A shell that still owned a search box would be a second one on screen.
 *
 * The client list's markup went to `ClientPicker`, its only caller, rather than to a component of
 * its own: extracting it would have created a file whose deletion puts forty lines back in one
 * place. The stylesheet stays shared — `ShelfQuestionDialog` already reads it for the same reason.
 *
 * ── `finalFocus` is not optional here ────────────────────────────────────────────────────────
 * Base UI restores focus to the element `Dialog.Trigger` registered, and neither of these dialogs
 * has a trigger — one is opened by a link in a bar, the other by the „Dodaj pytanie" button inside
 * a card. Without it focus lands on `<body>` and the next Tab restarts at the top of the document.
 * Verified against the installed source: `dialog/popup/DialogPopup.js` forwards `finalFocus` to
 * floating-ui as `returnFocus`.
 *
 * ── Whether a press CLOSES is the caller's ───────────────────────────────────────────────────
 * A client row toggles an assignment and the dialog stays open, because assigning five people is
 * five presses and a dialog that closed on each would be five openings. The exercise row is a single
 * choice and the caller closes on it. Neither decision is this file's.
 */

import type { ReactNode, RefObject } from 'react';
import { Dialog } from '@base-ui/react/dialog';

import { CrossIcon } from '~/shared/ui/AuthIcons';
import ember from '~/shared/ui/emberButton.module.css';

import type { TranslateKey } from '../model/translateKey';
import styles from './PickerDialog.module.css';

export interface PickerDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly lede: string;
  /** What the trainer picks from — a search field and a list, whichever kind this picker is. */
  readonly children: ReactNode;
  /** Widen the popup where the body asks for it. The exercise library's filter panel does. */
  readonly wide?: boolean;
  readonly footer?: ReactNode;
  readonly triggerRef: RefObject<HTMLElement | null>;
  readonly t: TranslateKey;
}

export function PickerDialog({
  open,
  onClose,
  title,
  lede,
  children,
  wide = false,
  footer,
  triggerRef,
  t,
}: PickerDialogProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup
          className={`${String(styles.popup)} ${wide ? String(styles.popupWide) : ''}`}
          finalFocus={triggerRef}
        >
          <div className={styles.head}>
            <div className={styles.headText}>
              <Dialog.Title className={styles.title}>{title}</Dialog.Title>
              <p className={styles.lede}>{lede}</p>
            </div>
            <Dialog.Close className={styles.close} aria-label={t('web:reports.editor.picker.close')}>
              <CrossIcon className={styles.closeIcon} />
            </Dialog.Close>
          </div>

          {children}

          <div className={styles.foot}>
            {footer}
            <button
              type="button"
              className={`${String(ember.ember)} ${String(styles.done)}`}
              onClick={onClose}
            >
              {t('web:reports.editor.picker.done')}
            </button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
